// Narration and speech — the single place in the app that makes sound from words.
// TTS_SCOPE.md §2 (three-tier architecture) and §7.2 (voice profiles / playback rate).
//
// SINGLE-IMPORTER RULE: nothing else in the app may touch `window.speechSynthesis`,
// `SpeechSynthesisUtterance`, or the narration <audio> element. Same discipline as
// qrscan.js/jsqr and qrcode.js/qrcode-generator — one module owns the API, everything
// else goes through this door. Before T1 this logic lived inline in SpellingTest, which
// is why it couldn't be reused by Money Quest (R10) or tested on its own.
//
// THE THREE TIERS, in order, per call:
//   1. Pre-generated MP3 from R2 (/tts/<hash>.mp3) — only when the caller supplies an
//      audioKey. Consistent voice on every device; this is the whole point of W2.3.
//   2. The device's own speechSynthesis — today's behaviour, unchanged, now the fallback.
//   3. Silence, reported back so the caller can show its "audio isn't available" state.
//
// Nothing here ever throws, and a missing clip is a completely ordinary outcome (a 404
// on /tts/* is expected, not an error) — a child mid-spelling-test must never lose audio
// because a clip hasn't been generated yet. T1 ships with no clips existing at all, so
// every call falls to tier 2 and behaviour is identical to before this module existed.
//
// No React imports (lib rule). No imports at all, in fact — same near-dependency-free
// posture as lib/content.js and lib/oinks.js, so this is safe to import from anywhere.
// Native Run A: narration clips come from the API host in a native build (lib/buildFlags.js).
import { apiUrl } from "./buildFlags.js";

// ------------------------------ device speech (tier 2) ------------------------------

// Picks the best available English voice — prefers en-GB (extra points for a name that sounds British),
// then any en-* voice, then whatever's first. Case-insensitive since browsers report lang tags inconsistently.
// Moved verbatim from Tests.jsx in T1.
export function pickEnglishVoice() {
  if (typeof window === "undefined" || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices || voices.length === 0) return null;
  const lang = (v) => (v.lang || "").toLowerCase();
  const soundsBritish = (v) => /uk|british|england|london|kate|daniel|serena|arthur/i.test(v.name || "");
  return (
    voices.find((v) => lang(v) === "en-gb" && soundsBritish(v)) ||
    voices.find((v) => lang(v) === "en-gb") ||
    voices.find((v) => lang(v).startsWith("en") && soundsBritish(v)) ||
    voices.find((v) => lang(v).startsWith("en")) ||
    voices[0]
  );
}

export function isSpeechAvailable() {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

// Browsers populate getVoices() asynchronously, and on some (notably Chrome) the first
// call returns an empty list. Poll briefly and also listen for onvoiceschanged. Returns a
// cleanup function the caller runs on unmount.
//
// Module-level, not per-component: `onvoiceschanged` is a single global handler slot, so
// two components each assigning it would silently clobber one another. Only ever one
// speech surface is mounted today, but Money Quest (T5) makes that no longer true.
export function primeVoices(onVoice) {
  if (!isSpeechAvailable()) return () => {};
  let attempts = 0;
  let timer = null;
  let cancelled = false;
  function tryLoad() {
    if (cancelled) return;
    const v = pickEnglishVoice();
    if (v) { onVoice(v); return; }
    attempts++;
    if (attempts < 10) timer = setTimeout(tryLoad, 300);
  }
  tryLoad();
  window.speechSynthesis.onvoiceschanged = tryLoad;
  return () => {
    cancelled = true;
    if (window.speechSynthesis) window.speechSynthesis.onvoiceschanged = null;
    if (timer) clearTimeout(timer);
  };
}

// The one-shot retry budget. Module-level rather than per-component because the thing it
// guards — the browser's speech queue — is itself global: two components with their own
// retry counters would be counting the same queue's failures twice.
let retried = false;
export function resetRetryBudget() { retried = false; }

// iOS Safari (especially as an installed app) commonly suspends the speech engine whenever
// the app is backgrounded — switching apps, locking the screen, even a brief app-switcher
// swipe — and the next speak() call can throw a spurious error even though nothing is
// actually broken. Callers wire this to visibilitychange/pageshow and, if it returns true,
// give audio another chance rather than leaving it disabled for the rest of the session.
export function resumeAfterForeground() {
  if (typeof document === "undefined" || document.visibilityState !== "visible") return false;
  if (typeof window === "undefined" || typeof window.speechSynthesis === "undefined") return false;
  window.speechSynthesis.cancel();
  retried = false;
  return true;
}

function speakWithDevice(text, opts) {
  const { rate = 1, isAutoplay = false, onUnavailable, onEnd = null } = opts || {};
  if (!isSpeechAvailable()) { if (!isAutoplay && onUnavailable) onUnavailable(); return false; }
  try {
    if (pendingAfter) { clearTimeout(pendingAfter); pendingAfter = null; } // a new word replaces a waiting line
    const v = pickEnglishVoice();
    const utter = new SpeechSynthesisUtterance(text);
    if (onEnd) utter.onend = onEnd;
    utter.rate = rate;
    utter.lang = v ? v.lang : "en-GB";
    if (v) utter.voice = v;
    // WebKit/iOS has a known bug where an utterance with no other live reference can be
    // silently garbage-collected while queued, cutting speech off after a handful of uses
    // with no error at all — keeping a ref alive here prevents that.
    liveUtterance = utter;
    // Browsers commonly block non-user-gesture playback (autoplay policy) and fire an error for it —
    // that's not a real failure, so autoplay errors are ignored outright. For a manual tap, retry
    // once (resetting the synthesis queue first) before treating it as truly unavailable — this
    // absorbs iOS hiccups (e.g. coming back from the background) without a page reload.
    utter.onerror = () => {
      if (isAutoplay) return;
      if (!retried) {
        retried = true;
        window.speechSynthesis.cancel();
        setTimeout(() => speakWithDevice(text, opts), 250);
        return;
      }
      if (onUnavailable) onUnavailable();
    };
    window.speechSynthesis.cancel();
    // Calling speak() in the same tick as cancel() can leave iOS's engine stuck reporting
    // "still speaking" forever, silently swallowing every speak() after — a short delay avoids it.
    setTimeout(() => window.speechSynthesis.speak(utter), 50);
    return true;
  } catch {
    if (isAutoplay) return false;
    if (!retried) { retried = true; setTimeout(() => speakWithDevice(text, opts), 250); return true; }
    if (onUnavailable) onUnavailable();
    return false;
  }
}

// See the GC note in speakWithDevice — this reference is the entire reason it exists.
let liveUtterance = null;

// ------------------------------ pre-generated clips (tier 1) ------------------------------

// ONE <audio> element for the life of the page, reused for every clip. Deliberate: on iOS
// an element is "unlocked" for programmatic playback by a user gesture, and that unlock
// belongs to the element, not the page — so a fresh element per clip would need a fresh
// gesture every time and autoplay-after-each-word would never work. Reusing one element
// means unlockAudio() below buys us every subsequent clip in the session.
let clipEl = null;
function getClipElement() {
  if (typeof document === "undefined") return null;
  if (!clipEl) {
    clipEl = document.createElement("audio");
    clipEl.preload = "auto";
    // Keeps a slowed-down clip sounding like a slower reading rather than a lower-pitched
    // one. Standard in current Chrome/Safari/Firefox; the prefixed names cover older WebKit
    // and Gecko. If none are honoured the clip still plays, just less pleasantly.
    clipEl.preservesPitch = true;
    clipEl.webkitPreservesPitch = true;
    clipEl.mozPreservesPitch = true;
  }
  return clipEl;
}

// Keys that came back 404 — remembered for the session so a word with no generated clip
// costs exactly one failed request, not one per listen. Cleared only by a page load, which
// is also when a newly published bundle would arrive.
const missingClips = new Set();

// The voice profiles, mirrored from worker/lib/tts-google.js. Duplicated deliberately
// rather than shared: this is a browser module and that is a Worker module, with no build
// step joining them, and the alternative (fetching profiles before the first word can be
// spoken) would put a network round-trip in front of a child's spelling test to save a
// four-line constant.
//
// THE COST OF THIS DUPLICATION IS REAL AND WORTH NAMING: if these strings ever drift from
// the Worker's, every hash computed here misses, every word silently falls back to the
// device voice, and nothing errors. Change one, change both — and the giveaway symptom is
// "generated audio stopped working everywhere at once, with no errors anywhere."
const CLIENT_VOICE_PROFILES = {
  teacher: "en-GB-Chirp3-HD-Sadaltager",
  narrator: "en-GB-Chirp3-HD-Sadaltager",
};
const CLIENT_TTS_MODEL = "chirp3-hd";

// Computes the clip id for a piece of text — the same content hash the Worker uses
// (worker/lib/tts-google.js audioKeyFor), so the two agree without ever talking.
//
// This is TTS_SCOPE.md §11.6's exception, and T3 is where it earns its keep beyond the
// custom-word case it was written for. Curriculum words live in the published bundle as
// word-list entries, and audio is generated AFTER publish — so there's no field on the
// entry to carry a clip id without republishing the whole bundle every time a backfill
// runs. Since the hash IS the text, the client can simply work it out. A word with no
// clip costs exactly one 404, cached in `missingClips` for the session.
//
// Money Quest (T5) still uses the bundle's `audio` field rather than this, because
// narration wants the duration up front for its progress bar and wants to know about
// absence without a failed request first.
export async function audioKeyFor(text, profileId) {
  try {
    if (!text || typeof crypto === "undefined" || !crypto.subtle) return null;
    const voice = CLIENT_VOICE_PROFILES[profileId] || CLIENT_VOICE_PROFILES.narrator;
    const input = `${CLIENT_TTS_MODEL}|${voice}|${text}`;
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
      .slice(0, 32);
  } catch {
    // No WebCrypto (an insecure origin, most likely) — null just means tier 2.
    return null;
  }
}

// Warms a clip so the caller knows, BEFORE it needs to speak, whether tier 1 exists.
//
// WHY (12 Sep staging pass, iOS): with an audioKey present, speak() awaited playClip, and a
// missing clip only revealed itself as a 404 partway through that await. The device-voice
// fallback was then issued from deep inside an async chain, and on iOS it produced silence —
// while a word with NO key at all, where speakWithDevice is called directly, spoke fine.
// Prefetching moves the 404 to when the word appears, so by the time we speak, a missing
// clip has already been recorded in `missingClips` and speak() goes straight down the same
// path that works. Never throws, never blocks: a failure here just leaves things as before.
export function prefetchClip(audioKey) {
  if (!audioKey || missingClips.has(audioKey)) return Promise.resolve(!missingClips.has(audioKey));
  return fetch(audioUrlFor(audioKey), { method: "GET", cache: "force-cache" })
    .then((r) => { if (!r || !r.ok) { missingClips.add(audioKey); return false; } return true; })
    .catch(() => false); // offline or blocked: leave it unknown rather than marking it missing
}

export function audioUrlFor(audioKey) {
  return apiUrl(`/tts/${audioKey}.mp3`);
}

// Plays a pre-generated clip. Resolves true if playback actually started, false for any
// other outcome — not generated yet, offline, blocked by autoplay policy, decode failure.
// False is not an error condition; it just means the caller should fall through to tier 2.
function playClip(audioKey, rate) {
  return new Promise((resolve) => {
    if (!audioKey || missingClips.has(audioKey)) { resolve(false); return; }
    const el = getClipElement();
    if (!el) { resolve(false); return; }
    let settled = false;
    const done = (ok) => {
      if (settled) return;
      settled = true;
      el.onerror = null;
      resolve(ok);
    };
    el.onerror = () => {
      // Covers a 404 (clip not generated yet) and a decode failure alike. Both mean the
      // same thing to the caller, and neither is worth logging.
      missingClips.add(audioKey);
      done(false);
    };
    try {
      el.onended = null; // a second line waiting on the last clip is not this clip's
      el.pause();
      el.src = audioUrlFor(audioKey);
      el.playbackRate = rate || 1;
      el.currentTime = 0;
      const p = el.play();
      // Older browsers return undefined from play() rather than a promise.
      if (p && typeof p.then === "function") p.then(() => done(true)).catch(() => done(false));
      else done(true);
    } catch {
      done(false);
    }
  });
}

// iOS blocks programmatic playback on an element that has never been played from inside a
// user gesture. Calling this from a real tap (SpellingTest's "Start Test" button) plays a
// silent data-URI clip on the shared element, which unlocks it for the autoplay that fires
// a second after each word appears.
//
// Without this, tier-1 audio would be LESS reliable than the speechSynthesis it replaces,
// on the platform that matters most — which would make the whole feature a regression.
// Safe to call repeatedly; safe to call where audio doesn't exist. Never throws.
export function unlockAudio() {
  try {
    const el = getClipElement();
    if (!el || el.dataset.lbUnlocked === "1") return;
    el.dataset.lbUnlocked = "1";
    // 0.05s of silence. Inline so there's no asset to host, no request to make, and nothing
    // that can 404 on the one call that has to succeed.
    el.src = "data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tAwAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAACAAABIADAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDA//////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAASDs90hvAAAAAAAAAAAAAAAAAAAA//sQxAADwAABpAAAACAAADSAAAAETEFNRTMuMTAwVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV";
    const p = el.play();
    if (p && typeof p.then === "function") p.then(() => el.pause()).catch(() => {});
    else el.pause();
  } catch {
    // An unlock that fails just means tier 1 may not autoplay on this device. Tier 2 is
    // still there, and a manual tap still works — never worth interrupting anything for.
  }
}

// ------------------------------ the public door ------------------------------

// speak(text, opts) — say `text` using the best tier available.
//
//   audioKey    pre-generated clip id, when the caller has one. Absent (the T1 case, and
//               always the case for a parent's own custom words) means straight to tier 2.
//   rate        playback speed. 0.85 for spelling prompts, 1 for narration (TTS_SCOPE.md
//               §7.2) — applied identically to a generated clip and to device speech, so
//               the two tiers now slow down by the same rule rather than by coincidence.
//               Deliberately a PLAYBACK property, not a generation one: Chirp 3: HD takes
//               no speakingRate parameter, and keeping rate out of the clip means one clip
//               serves every speed and re-tuning costs nothing.
//   isAutoplay  true when triggered by a timer rather than a tap. Autoplay failures are
//               expected (browser autoplay policy) and are swallowed rather than reported.
//   onUnavailable  called only when a MANUAL attempt has exhausted its retry and nothing
//               can speak — i.e. tier 3. The caller uses it to show its own fallback text.
//
// Resolves true if something is playing or queued.
//   after       optional second line, spoken by the device voice once `text` has finished,
//               after a pause (Alan, 5 Oct: "There needs to be a slight pause between the word
//               and 'as in'"). A full stop between them was all the pause a device voice gave.
//               A clip already carries both, so this only applies when the device speaks.
//   afterKey    the second line's own clip (Alan, 5 Oct: "The as ins haven't run through TTS"):
//               played after the pause, whichever tier spoke the first line; the device voice
//               reads the line when it has no clip yet.
//   pauseMs     that pause; 700 by default.
export async function speak(text, opts = {}) {
  if (!text) return false;
  // afterDeviceOnly: the clip already says both lines, so `after` is for the device tier alone.
  const { audioKey = null, rate = 1, isAutoplay = false, onUnavailable = null, after = "", afterKey = null, afterDeviceOnly = false, pauseMs = 700 } = opts;
  const afterText = String(after || "").trim();
  const thenAfter = afterText ? () => {
    pendingAfter = setTimeout(async () => {
      pendingAfter = null;
      if (afterKey && await playClip(afterKey, rate)) return;
      speakWithDevice(afterText, { rate, isAutoplay: true });
    }, pauseMs);
  } : null;
  if (audioKey) {
    const played = await playClip(audioKey, rate);
    if (played) { if (thenAfter && !afterDeviceOnly) onClipEnded(thenAfter); return true; }
    // Fall through to device speech — no clip is an ordinary state, not a failure.
  }
  return speakWithDevice(text, { rate, isAutoplay, onUnavailable, onEnd: thenAfter });
}
// Runs `fn` once when the clip now playing finishes. playClip clears it, so a new word never
// inherits the last one's second line.
function onClipEnded(fn) {
  const el = getClipElement();
  if (!el) return;
  el.onended = () => { el.onended = null; fn(); };
}
// The second line waiting out its pause, so stopSpeaking can stop it too.
let pendingAfter = null;

// Stops whatever is currently speaking, on either tier. Used when a test is quit or a
// component unmounts, so a word doesn't keep talking over the screen that replaced it.
export function stopSpeaking() {
  try {
    if (pendingAfter) { clearTimeout(pendingAfter); pendingAfter = null; }
    if (isSpeechAvailable()) window.speechSynthesis.cancel();
    if (clipEl) { clipEl.onended = null; clipEl.pause(); }
  } catch {
    // Nothing here is worth an error path.
  }
}
